import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { formatDate, cn, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import {
  CalendarDays,
  Plus,
  Clock,
  Users,
  Phone,
  Mail,
  Check,
  X,
  Edit,
  Trash2,
} from "lucide-react";

const ReservationsPage = () => {
  const { t } = useLanguage();
  const { token, isManager } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    table_id: "",
    date: new Date(),
    time: "19:00",
    guests: 2,
    notes: "",
    status: "pending",
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [resRes, tableRes] = await Promise.all([
        axios.get(`${API_URL}/reservations`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/tables`, { headers, withCredentials: true }),
      ]);
      setReservations(resRes.data);
      setTables(tableRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const reservationData = {
        ...form,
        date: format(form.date, "yyyy-MM-dd"),
      };

      if (editingReservation) {
        // Update reservation (API endpoint to be implemented)
        toast.success("Reserva atualizada!");
      } else {
        // Create reservation (API endpoint to be implemented)
        // For now, add to local state
        const newReservation = {
          reservation_id: `res_${Date.now()}`,
          ...reservationData,
          created_at: new Date().toISOString(),
        };
        setReservations([...reservations, newReservation]);
        toast.success("Reserva criada!");
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Erro ao guardar reserva");
    }
  };

  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
    setForm({
      customer_name: reservation.customer_name,
      customer_phone: reservation.customer_phone || "",
      customer_email: reservation.customer_email || "",
      table_id: reservation.table_id,
      date: new Date(reservation.date),
      time: reservation.time,
      guests: reservation.guests,
      notes: reservation.notes || "",
      status: reservation.status,
    });
    setModalOpen(true);
  };

  const handleDelete = (reservationId) => {
    if (!window.confirm("Cancelar esta reserva?")) return;
    setReservations(reservations.filter((r) => r.reservation_id !== reservationId));
    toast.success("Reserva cancelada!");
  };

  const handleStatusChange = (reservationId, status) => {
    setReservations(
      reservations.map((r) =>
        r.reservation_id === reservationId ? { ...r, status } : r
      )
    );
    toast.success("Estado atualizado!");
  };

  const resetForm = () => {
    setEditingReservation(null);
    setForm({
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      table_id: "",
      date: new Date(),
      time: "19:00",
      guests: 2,
      notes: "",
      status: "pending",
    });
  };

  const timeSlots = Array.from({ length: 28 }, (_, i) => {
    const hour = Math.floor(i / 2) + 11;
    const minute = (i % 2) * 30;
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  });

  const getStatusBadge = (status) => {
    const statuses = {
      pending: { label: "Pendente", class: "bg-amber-500/20 text-amber-500" },
      confirmed: { label: "Confirmada", class: "bg-emerald-500/20 text-emerald-500" },
      cancelled: { label: "Cancelada", class: "bg-red-500/20 text-red-500" },
      completed: { label: "Concluída", class: "bg-slate-500/20 text-slate-400" },
    };
    return statuses[status] || statuses.pending;
  };

  const filteredReservations = reservations.filter(
    (r) => r.date === format(selectedDate, "yyyy-MM-dd")
  );

  const getTableName = (tableId) => {
    return tables.find((t) => t.table_id === tableId)?.name || "Mesa não definida";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reservations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Reservas</h1>
          <p className="text-muted-foreground">Gestão de reservas do restaurante</p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }} className="gap-2" data-testid="new-reservation-btn">
          <Plus className="w-4 h-4" />
          Nova Reserva
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <CalendarDays className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{filteredReservations.length}</p>
                <p className="text-sm text-muted-foreground">Hoje</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {filteredReservations.filter((r) => r.status === "pending").length}
                </p>
                <p className="text-sm text-muted-foreground">Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Check className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {filteredReservations.filter((r) => r.status === "confirmed").length}
                </p>
                <p className="text-sm text-muted-foreground">Confirmadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {filteredReservations.reduce((sum, r) => sum + r.guests, 0)}
                </p>
                <p className="text-sm text-muted-foreground">Pessoas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar & Reservations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="font-['Outfit']">Calendário</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={pt}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        {/* Reservations List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Outfit']">
              Reservas - {format(selectedDate, "d 'de' MMMM", { locale: pt })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredReservations.length > 0 ? (
              <div className="space-y-4">
                {filteredReservations
                  .sort((a, b) => a.time.localeCompare(b.time))
                  .map((reservation) => {
                    const statusInfo = getStatusBadge(reservation.status);
                    return (
                      <div
                        key={reservation.reservation_id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card"
                        data-testid={`reservation-${reservation.reservation_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold font-mono">{reservation.time}</p>
                          </div>
                          <div>
                            <h4 className="font-semibold">{reservation.customer_name}</h4>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {reservation.guests} pessoas
                              </span>
                              <span>{getTableName(reservation.table_id)}</span>
                            </div>
                            {reservation.customer_phone && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <Phone className="w-3 h-3" />
                                {reservation.customer_phone}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={statusInfo.class}>{statusInfo.label}</Badge>
                          {reservation.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(reservation.reservation_id, "confirmed")}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(reservation)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDelete(reservation.reservation_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma reserva para este dia</p>
                <Button variant="outline" className="mt-4" onClick={() => setModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Reserva
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="reservation-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingReservation ? "Editar Reserva" : "Nova Reserva"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Cliente</Label>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                required
                data-testid="reservation-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder="+351..."
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      {format(form.date, "dd/MM/yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(date) => date && setForm({ ...form, date })}
                      locale={pt}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Hora</Label>
                <Select value={form.time} onValueChange={(value) => setForm({ ...form, time: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nº de Pessoas</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={form.guests}
                  onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Mesa</Label>
                <Select value={form.table_id} onValueChange={(value) => setForm({ ...form, table_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tables.map((table) => (
                      <SelectItem key={table.table_id} value={table.table_id}>
                        Mesa {table.number} - {table.name} ({table.capacity} lugares)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Preferências, alergias, ocasião especial..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                {t("cancel")}
              </Button>
              <Button type="submit" data-testid="save-reservation-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReservationsPage;
